import { createTheme, alpha } from "@mui/material/styles";
import {
  BrandColors,
  NeutralColors,
  BorderRadius,
  Typography as BrandTypography,
} from "@neumind/shared/branding";

// Marketing-specific extensions (more dramatic for landing pages)
const MarketingExtensions = {
  // Larger headings for marketing impact
  heroFontSize: {
    h1: { xs: "2.75rem", md: "4rem" },
    h2: { xs: "2rem", md: "3rem" },
  },
  // Richer gradients for sections
  gradients: {
    primary: `linear-gradient(135deg, ${alpha(BrandColors.primary.main, 0.08)} 0%, ${alpha(BrandColors.secondary.main, 0.08)} 100%)`,
    hero: `linear-gradient(135deg, ${alpha(BrandColors.primary.main, 0.12)} 0%, ${alpha(BrandColors.secondary.main, 0.12)} 100%)`,
    dark: `linear-gradient(135deg, ${alpha(BrandColors.primary.main, 0.15)} 0%, ${alpha(BrandColors.primary.dark, 0.1)} 40%, transparent 100%)`, // Subtle overlay on dark background
    ctaCard: `radial-gradient(circle at 0% 0%, #2a3d90 0%, ${NeutralColors[900]} 100%)`, // Lit from top-left, brighter blue
  },
  // Animation timings
  transitions: {
    fast: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
    medium: "300ms cubic-bezier(0.4, 0, 0.2, 1)",
    slow: "500ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  // Shadows for depth — navy-tinted (#1B3A6B → rgba(27,58,107,x)) per the
  // visual-language brief: every shadow belongs to the brand family, never
  // pure black/cold grey. Scale mirrors the product theme --shadow-1..4.
  shadows: {
    card: "0 1px 2px 0 rgba(27,58,107,0.06), 0 1px 4px 0 rgba(27,58,107,0.04)", // --shadow-1: resting cards
    cardHover: "0 2px 8px -1px rgba(27,58,107,0.09), 0 1px 3px 0 rgba(27,58,107,0.05)", // --shadow-2: hovered card/nav
    hero: "0 16px 40px -8px rgba(27,58,107,0.14), 0 4px 12px -2px rgba(27,58,107,0.08)", // --shadow-4: floating hero artefact
    focus: `0 0 0 3px ${alpha(BrandColors.primary.main, 0.25)}`,
  },
  // Standardized icon sizes
  iconSizes: {
    sm: 20,
    md: 24,
    lg: 28,
    xl: 40,
  },
};

/**
 * Numeric color scale interface (Tailwind-compatible)
 */
export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
  main: string;
  light: string;
  dark: string;
  contrastText: string;
}

/**
 * Audience-based color palettes
 * Uses standard Tailwind scales for consistency
 */
export const AudienceColors = {
  // Clinician: Blue (Matches BrandColors.primary)
  clinician: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb", // Brand Primary
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a",
    950: "#172554",
    main: "#2563eb",
    light: "#60a5fa",
    dark: "#1e3a8a",
    contrastText: "#ffffff",
  },
  // Individual/Family: Indigo
  individual: {
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1",
    600: "#4f46e5", // Indigo-600
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
    950: "#1e1b4b",
    main: "#4f46e5",
    light: "#818cf8",
    dark: "#3730a3",
    contrastText: "#ffffff",
  },
  // Other: Cyan (Matches BrandColors.secondary)
  other: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#a5f3fc",
    300: "#67e8f9",
    400: "#22d3ee",
    500: "#06b6d4", // Brand Secondary
    600: "#0891b2",
    700: "#0e7490",
    800: "#155e75",
    900: "#164e63",
    950: "#083344",
    main: "#06b6d4",
    light: "#67e8f9",
    dark: "#0e7490",
    contrastText: "#ffffff",
  },
  // Sky: Light blue accent
  sky: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9",
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49",
    main: "#0ea5e9",
    light: "#38bdf8",
    dark: "#0369a1",
    contrastText: "#ffffff",
  },
};

// Extend Material UI Palette to include audience
declare module "@mui/material/styles" {
  interface Palette {
    audience: typeof AudienceColors;
  }
  interface PaletteOptions {
    audience?: typeof AudienceColors;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: BrandColors.primary.main,
      light: BrandColors.primary.light,
      dark: BrandColors.primary.dark,
      contrastText: BrandColors.primary.contrast,
    },
    secondary: {
      main: BrandColors.secondary.main,
      light: BrandColors.secondary.light,
      dark: BrandColors.secondary.dark,
      contrastText: BrandColors.secondary.contrast,
    },
    success: BrandColors.success,
    error: BrandColors.error,
    warning: BrandColors.warning,
    info: BrandColors.info,
    background: {
      default: "#ffffff",
      paper: "#ffffff"
    },
    text: {
      primary: NeutralColors[900],
      secondary: NeutralColors[600],
    },
    grey: NeutralColors,
    audience: AudienceColors,
    // Navy-tinted hairline (rgba(27,58,107,0.08)) so default dividers/borders
    // belong to the same brand family as the navy-tinted shadow scale, rather
    // than reading as a cold grey. Per the visual-language brief.
    divider: "rgba(27,58,107,0.08)",
  },
  typography: {
    fontFamily: `"Assistant", ${BrandTypography.fontFamily.sans}`,
    h1: {
      fontSize: "3.5rem",
      fontWeight: BrandTypography.fontWeight.bold,
      lineHeight: 1.15,
      letterSpacing: "-0.02em",
      "@media (max-width:600px)": {
        fontSize: "2.5rem",
      },
    },
    h2: {
      fontSize: "2.75rem",
      fontWeight: BrandTypography.fontWeight.bold,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
      "@media (max-width:600px)": {
        fontSize: "2rem",
      },
    },
    h3: {
      fontSize: "1.875rem",
      fontWeight: BrandTypography.fontWeight.semibold,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "1.375rem",
      fontWeight: BrandTypography.fontWeight.semibold,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: BrandTypography.fontWeight.semibold,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: BrandTypography.fontWeight.semibold,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1.125rem",
      lineHeight: 1.7,
      color: NeutralColors[600],
    },
    body2: {
      fontSize: "1rem",
      lineHeight: 1.6,
      color: NeutralColors[500],
    },
    button: {
      fontWeight: BrandTypography.fontWeight.semibold,
      textTransform: "none",
    },
    overline: {
      fontWeight: BrandTypography.fontWeight.medium,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
  },
  shape: {
    // DS-ADR 0009: www base stays 6 until sites/www/src has zero unitless borderRadius literals (scripts/lib/style-literal-baseline.txt), then 12.
    borderRadius: 6,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: () => ({
          textTransform: "none",
          borderRadius: BorderRadius.md,
          fontWeight: BrandTypography.fontWeight.semibold,
          padding: "12px 28px",
          transition: MarketingExtensions.transitions.medium,
          "&:focus-visible": {
            outline: "none",
            boxShadow: MarketingExtensions.shadows.focus,
          },
        }),
        contained: {
          boxShadow: "0 2px 8px rgba(77, 101, 255, 0.25)",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(77, 101, 255, 0.35)",
          },
          "&:active": {
            boxShadow: "0 2px 8px rgba(77, 101, 255, 0.25)",
          },
        },
        outlined: {
          borderWidth: "2px",
          "&:hover": {
            borderWidth: "2px",
            backgroundColor: alpha(BrandColors.primary.main, 0.04),
          },
        },
        sizeLarge: {
          padding: "14px 32px",
          fontSize: "1.0625rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: BorderRadius.lg,
          boxShadow: MarketingExtensions.shadows.card,
          border: "1px solid rgba(27,58,107,0.08)", // navy-tinted hairline, same family as the shadow scale
          transition: MarketingExtensions.transitions.medium,
          "&:hover": {
            boxShadow: MarketingExtensions.shadows.cardHover,
            transform: "translateY(-4px)",
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: MarketingExtensions.shadows.focus,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          transition: MarketingExtensions.transitions.fast,
          "&:focus-visible": {
            outline: "none",
            boxShadow: MarketingExtensions.shadows.focus,
            borderRadius: BorderRadius.sm,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          "@media (min-width:1200px)": {
            maxWidth: "1200px",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: BorderRadius.md,
          fontWeight: BrandTypography.fontWeight.medium,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: BorderRadius.md,
            transition: MarketingExtensions.transitions.fast,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: BrandColors.primary.light,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: BrandColors.primary.main,
              boxShadow: `0 0 0 3px ${alpha(BrandColors.primary.main, 0.1)}`,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(12px)",
          boxShadow: "0 1px 3px rgba(27,58,107,0.06)", // navy-tinted nav hairline shadow
        },
      },
    },
  },
});

// Export marketing extensions for use in components
export const marketing = MarketingExtensions;